<template>
  <div>
    <v-dialog
      v-model="dialog"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      transition="dialog-bottom-transition"
      content-class="item-booking-dialog"
      max-width="560"
      scrollable
    >
      <v-card v-if="item" color="product_card" :style="bookingCardStyle">
        <v-toolbar flat dense color="tertiary lighten-3">
          <v-toolbar-title class="body-1 font-weight-bold product_name--text">
            {{ item.name }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider class="my-0" />

        <v-card-text class="pa-0">
          <!--start item header-->
          <div class="pa-3">
            <v-img
              v-if="item.image || Images.product_image"
              class="item-image mb-3"
              :src="item.image || Images.product_image"
              :alt="item.name"
              height="180"
              @click="openImage(item.image || Images.product_image, item.name)"
            />

            <div
              v-if="!hideProductDescriptionInMenu && item.description"
              class="product_description--text body-2 mb-2"
              v-html="item.description"
            ></div>

            <div
              v-if="parseFloat(itemDiscount) > 0"
              class="d-flex align-center"
            >
              <span class="product_price--text" id="discounted">{{
                (item.finalizedPrice + itemDeposit) | currency
              }}</span>
              <span class="ml-2" id="oldPrice">{{
                (item.taxedPrice + itemDeposit) | currency
              }}</span>
            </div>
            <h6 v-else class="product_price--text">
              {{ (item.taxedPrice + itemDeposit) | currency }}
            </h6>

            <div v-if="itemDeposit > 0" class="text-caption text-muted">
              {{ $t("generic.lang_includeDeposit") }}
              {{ itemDeposit | currency }}
            </div>

            <v-btn
              v-if="!hideProductDetails"
              class="accent--text px-0 mt-1"
              icon
              @click="details_dialog = true"
            >
              <v-icon>mdi-information</v-icon>
            </v-btn>
          </div>
          <!--end item header-->

          <v-divider class="my-0" />

          <div class="tertiary lighten-3 pt-3 pb-1 px-3">
            <!--start sizes-->
            <div v-if="hasSize" class="pb-2">
              <strong>{{ $t("generic.lang_meal_sizes") }}</strong>

              <v-radio-group v-model="size" class="mt-1">
                <v-radio
                  v-for="mealSize in item.sizes"
                  :key="mealSize.id"
                  :value="mealSize"
                >
                  <template v-slot:label>
                    <div>
                      <strong>{{ mealSize.sizeName }}</strong>

                      <span
                        v-if="parseFloat(itemDiscount) > 0"
                        class="d-inline-flex align-center"
                      >
                        <span class="primary--text ml-2" id="discounted-size">{{
                          (mealSize.finalizedPrice + itemDeposit) | currency
                        }}</span>
                        <span class="ml-2" id="oldPrice-size">{{
                          (mealSize.sellPrice + itemDeposit) | currency
                        }}</span>
                      </span>
                      <span v-else class="primary--text ml-2">
                        {{ (mealSize.sellPrice + itemDeposit) | currency }}
                      </span>

                      <div
                        v-if="itemDeposit > 0"
                        class="text-caption text-muted"
                      >
                        {{ $t("generic.lang_includeDeposit") }}
                        {{ itemDeposit | currency }}
                      </div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </div>
            <!--end sizes-->

            <!--start extras-->
            <div v-if="!hasSize || currentMealSize !== null" class="pb-2">
              <strong>{{ $t("generic.lang_extras") }}</strong>

              <v-expansion-panels
                v-model="openedPanel"
                accordion
                flat
                class="elevation-0 pa-0 ma-0 pt-2 transparent"
              >
                <v-expansion-panel
                  v-for="tab in item.extras"
                  ref="panel"
                  :key="tab.id"
                  v-show="canShowExtrasTab(tab)"
                  class="elevation-0 pa-0 ma-0 transparent"
                >
                  <v-expansion-panel-header class="elevation-0 pa-0 pl-2 ma-0">
                    <h5 class="text-muted">
                      {{
                        tab.invertGarnishSelection
                          ? $t("generic.lang_without")
                          : ""
                      }}
                      {{ tab.tabName }}
                    </h5>
                    <v-spacer />
                    <span
                      class="font-weight-bolder text-right"
                      :class="
                        (tab.maxChoose > 0 &&
                          totalQuantityOfSelectedExtrasInTab(tab) >=
                            tab.maxChoose) ||
                        tab.minChoose < 1
                          ? 'text-success'
                          : 'text-danger'
                      "
                    >
                      {{
                        tab.minChoose >= 1
                          ? $t("generic.lang_required")
                          : $t("generic.lang_optional")
                      }}
                    </span>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="less-padding">
                    <div
                      v-if="showGarnishImages(tab)"
                      class="d-flex flex-wrap px-1"
                    >
                      <div
                        v-for="ext in visibleExtras(tab)"
                        :key="tab.id + '-' + ext.id"
                        class="garnish-card text-center pa-1"
                      >
                        <div class="garnish-avatar-wrap">
                          <v-avatar
                            tile
                            size="72"
                            color="accent"
                            class="mb-1 rounded-lg"
                            :style="garnishStyle"
                            @click="openGarnishImage(ext)"
                          >
                            <img
                              v-if="garnishImage(ext)"
                              :src="garnishImage(ext)"
                              :alt="ext.name"
                              loading="lazy"
                              decoding="async"
                              @error="markGarnishImageBroken(ext)"
                            />
                            <span v-else class="white--text text-h6">{{
                              garnishInitial(ext)
                            }}</span>
                          </v-avatar>

                          <div
                            v-if="isRemovedGarnish(tab, ext)"
                            class="garnish-removed-overlay"
                          >
                            <v-icon color="error" large>mdi-close</v-icon>
                          </div>
                        </div>

                        <div
                          class="caption"
                          :class="{
                            'booked-garnish': isRemovedGarnish(tab, ext)
                          }"
                        >
                          {{ ext.name }}
                          <span
                            v-if="parseFloat(ext.finalizedPrice) > 0"
                            class="primary--text"
                            >{{ ext.finalizedPrice | currency }}</span
                          >
                        </div>

                        <div v-if="!isReadonly">
                          <v-btn
                            width="28px"
                            height="28px"
                            class="rounded-circle pa-0 px-0"
                            color="accent"
                            icon
                            outlined
                            rounded
                            x-small
                            :disabled="!isExtrasSelected(ext)"
                            @click="pushExtras(false, ext, tab)"
                          >
                            <v-icon class="pa-0 px-0" small>mdi-minus</v-icon>
                          </v-btn>

                          <span class="mr-2 ml-2">{{
                            extraQuantity(tab, ext)
                          }}</span>

                          <v-btn
                            width="30px"
                            height="30px"
                            class="rounded-circle pa-0"
                            color="accent"
                            icon
                            outlined
                            rounded
                            x-small
                            :disabled="
                              (tab.maxChoose !== 0 &&
                                totalQuantityOfSelectedExtrasInTab(tab) >=
                                  tab.maxChoose) ||
                                (tab.invertGarnishSelection &&
                                  extraQuantity(tab, ext) > 0)
                            "
                            @click="pushExtras(true, ext, tab)"
                          >
                            <v-icon small>mdi-plus</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </div>

                    <v-list v-else class="px-0 mx-0 transparent">
                      <v-list-item
                        v-for="ext in visibleExtras(tab)"
                        :key="tab.id + '-' + ext.id"
                        class="px-0 mx-0 pt-1 pb-1"
                      >
                        <v-list-item-content class="px-0 mx-0 pt-0 pb-0">
                          <v-list-item-title
                            :class="{
                              'booked-garnish': isRemovedGarnish(tab, ext)
                            }"
                          >
                            {{ ext.name }}
                            <span
                              v-if="parseFloat(ext.finalizedPrice) > 0"
                              class="primary--text"
                              >{{ ext.finalizedPrice | currency }}</span
                            >
                          </v-list-item-title>

                          <div v-if="!isReadonly" class="mx-auto">
                            <v-btn
                              width="28px"
                              height="28px"
                              class="rounded-circle pa-0 px-0"
                              color="accent"
                              icon
                              outlined
                              rounded
                              x-small
                              :disabled="!isExtrasSelected(ext)"
                              @click="pushExtras(false, ext, tab)"
                            >
                              <v-icon class="pa-0 px-0" small>mdi-minus</v-icon>
                            </v-btn>

                            <span class="mr-2 ml-2">{{
                              extraQuantity(tab, ext)
                            }}</span>

                            <v-btn
                              width="30px"
                              height="30px"
                              class="rounded-circle pa-0"
                              color="accent"
                              icon
                              outlined
                              rounded
                              x-small
                              :disabled="
                                (tab.maxChoose !== 0 &&
                                  totalQuantityOfSelectedExtrasInTab(tab) >=
                                    tab.maxChoose) ||
                                  (tab.invertGarnishSelection &&
                                    extraQuantity(tab, ext) > 0)
                              "
                              @click="pushExtras(true, ext, tab)"
                            >
                              <v-icon small>mdi-plus</v-icon>
                            </v-btn>
                          </div>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>

                    <div v-if="extrasFilter_2(tab).length > 3">
                      <v-btn
                        v-if="!showAllExtras"
                        block
                        text
                        color="primary"
                        @click="showAllExtras = true"
                      >
                        <v-icon>mdi-chevron-down</v-icon>
                        {{ $t("generic.lang_show_more") }} ({{
                          extrasFilter_2(tab).length
                        }})
                      </v-btn>

                      <v-btn
                        v-else
                        block
                        text
                        color="primary"
                        @click="showAllExtras = false"
                      >
                        <v-icon>mdi-chevron-up</v-icon>
                        {{ $t("generic.lang_show_less") }} (3)
                      </v-btn>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
            <!--end extras-->
          </div>
        </v-card-text>

        <v-divider class="my-0" />

        <v-card-actions v-if="!isReadonly" class="pa-3 flex-nowrap">
          <div class="qty-control d-flex align-center flex-shrink-0 mr-3">
            <v-btn icon small color="accent" @click="minus">
              <v-icon small>mdi-minus</v-icon>
            </v-btn>
            <span class="mx-2 body-2 font-weight-bold">{{ quantity }}</span>
            <v-btn icon small color="accent" @click="plus">
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
          </div>

          <v-btn
            height="40px"
            class="flex-grow-1"
            min-width="0"
            elevation="0"
            color="primary"
            :disabled="!isValidExtrasLimits"
            @click="addToCard(item)"
          >
            {{ currentTotal | currency }}
            <strong v-if="parseFloat(itemDiscount) > 0" class="ml-1">
              ({{ itemDiscount + "%" }})
            </strong>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <product-info-dialog
      v-if="item"
      v-model="details_dialog"
      :dialog="details_dialog"
      :item="item"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import itemBooking from "@/mixins/itemBooking";
import ProductInfoDialog from "@/components/Menu/ProductInfoDialog";

export default {
  name: "ItemBookingDialog",

  components: { ProductInfoDialog },

  mixins: [itemBooking],

  data() {
    return {
      details_dialog: false,
      showAllExtras: false,
      openedPanel: null,
      //the store getter clones the size, so keep the picked object locally
      size: null
    };
  },

  computed: {
    ...createNamespacedHelpers("Order").mapGetters([
      "current",
      "currentQuantity",
      "currentMealSize",
      "isExtrasSelected",
      "ExtrasQuantity",
      "totalQuantityOfSelectedExtrasInTab",
      "itemTotalPrice"
    ]),
    ...createNamespacedHelpers("Config").mapGetters([
      "hideProductDetails",
      "hideProductDescriptionInMenu",
      "productCardStyle"
    ]),

    //the config getter ships the radius with an !important suffix
    cardRadius() {
      const radius = this.productCardStyle["border-radius"];

      return radius
        ? String(radius)
            .replace("!important", "")
            .trim()
        : null;
    },

    //same card styling as the menu row, fullscreen has no edges to style
    bookingCardStyle() {
      return this.$vuetify.breakpoint.smAndDown ? {} : this.productCardStyle;
    },

    garnishStyle() {
      return this.cardRadius ? { "border-radius": this.cardRadius } : {};
    },

    //the booked item lives in the store, the dialog only mirrors it
    item() {
      return this.current && this.current.item && this.current.item.id
        ? this.current.item
        : null;
    },

    discount() {
      return this.item ? this.item.discount : 0;
    },

    categoryId() {
      return this.item && this.item.category_id !== undefined
        ? this.item.category_id
        : null;
    },

    dialog: {
      get() {
        return !!this.item;
      },
      set(val) {
        if (!val) this.$store.commit("Order/selectCurrent", {});
      }
    },

    quantity: {
      get() {
        return this.currentQuantity;
      },
      set(val) {
        this.setCurrentQuantity(val);
      }
    },

    currentTotal() {
      return this.current ? this.itemTotalPrice(this.current) : 0;
    },

    hasExtrasForCurrentSize() {
      return garnish => {
        if (!Array.isArray(garnish) || !this.currentMealSize) return false;
        return !!garnish.find(gn => gn.mealsizeId === this.currentMealSize.id);
      };
    },

    canShowExtrasTab() {
      return tab => {
        if (!this.item) return false;
        if (this.hasSize) return this.hasExtrasForCurrentSize(tab.garnish);
        return true;
      };
    },

    canShowExtra() {
      return ext => {
        if (this.hasSize && this.currentMealSize !== null) {
          return ext.active && ext.mealsizeId === this.currentMealSize.id;
        }
        return ext.mealsizeId === 0 && ext.active;
      };
    },

    extrasFilter_2() {
      return tab => {
        if (Array.isArray(tab.garnish) && tab.garnish.length > 0) {
          return tab.garnish.filter(g => this.canShowExtra(g));
        }
        return [];
      };
    },

    isValidExtrasLimits() {
      if (!this.item) return false;
      if (this.hasSize && !this.currentMealSize) return false;
      if (Array.isArray(this.item.extras)) {
        return this.checkExtrasLimits([...this.item.extras]) === 1;
      }
      return false;
    }
  },

  watch: {
    item(val) {
      this.showAllExtras = false;
      this.size = null;
      //nothing to pick before the extras, open the first tab right away
      this.openedPanel =
        val && !this.hasSize ? this.firstVisibleTabIndex() : null;
      this.details_dialog = val ? this.details_dialog : false;
    },

    //picking a size rebuilds the tabs, open the first visible one right away
    size(val) {
      this.setCurrentMealSize(val === undefined ? null : val);
      this.showAllExtras = false;
      this.openedPanel =
        val || !this.hasSize ? this.firstVisibleTabIndex() : null;
    },

    //covers manual clicks too, the model is the only source of truth
    openedPanel(val) {
      this.showAllExtras = false;
      this.onPanelChange(val);
    },

    //the dialog wrapper is rendered outside this tree, feed it a css var
    cardRadius: {
      immediate: true,
      handler(val) {
        document.documentElement.style.setProperty(
          "--booking-dialog-radius",
          val || "12px"
        );
      }
    }
  },

  methods: {
    ...createNamespacedHelpers("Order").mapMutations([
      "setCurrentQuantity",
      "pushExtrasToCurrent",
      "removeExtraFromCurrent",
      "setCurrentMealSize"
    ]),

    firstVisibleTabIndex() {
      const tabs = Array.isArray(this.item && this.item.extras)
        ? this.item.extras
        : [];
      const index = tabs.findIndex(tab => this.canShowExtrasTab(tab));

      return index < 0 ? null : index;
    },

    visibleExtras(tab) {
      const extras = this.extrasFilter_2(tab);
      return this.showAllExtras ? extras : extras.slice(0, 3);
    },

    extraQuantity(tab, ext) {
      const qty = parseInt(
        this.ExtrasQuantity({ id: ext.id, tab_id: tab.id }),
        10
      );
      return isNaN(qty) ? 0 : qty;
    },

    isRemovedGarnish(tab, ext) {
      return !!tab.invertGarnishSelection && this.extraQuantity(tab, ext) > 0;
    },

    openGarnishImage(ext) {
      this.openImage(this.garnishImageFull(ext), ext.name);
    },

    onPanelChange(index) {
      if (index === null || index === undefined) return;

      window.clearTimeout(this._scrollTimer);
      this._scrollTimer = window.setTimeout(() => {
        const panel = this.$refs.panel && this.$refs.panel[index];

        if (panel && panel.$el && panel.$el.scrollIntoView) {
          panel.$el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 320);
    },

    checkExtrasLimits(tabs) {
      if (!Array.isArray(tabs) || tabs.length === 0) return 1;
      let flag = 1;

      const tab = { ...tabs[0] };
      if (Array.isArray(tab.garnish) && this.canShowExtrasTab(tab)) {
        if (
          tab.maxChoose > 0 &&
          this.totalQuantityOfSelectedExtrasInTab(tab) > tab.maxChoose
        )
          flag = 0;
        if (
          tab.minChoose > 0 &&
          this.totalQuantityOfSelectedExtrasInTab(tab) < tab.minChoose
        )
          flag = 0;
      }

      tabs.shift();

      return flag * this.checkExtrasLimits(tabs);
    },

    pushExtras(value, ext, tab) {
      ext.tab_id = tab.id;

      if (value) {
        if (tab.invertGarnishSelection && this.extraQuantity(tab, ext) > 0) {
          return;
        }

        this.pushExtrasToCurrent({ ext, tab });
      } else {
        this.removeExtraFromCurrent(ext);
      }
    },

    minus() {
      if (this.quantity > 1) this.quantity = this.quantity - 1;
    },

    plus() {
      if (this.item && this.item.canBeSoldOncePerOrder) {
        this.$swal({
          title: this.$t("generic.lang_thisItemCanOnlyBeSoldOncePerOrder"),
          icon: "info"
        });
        return;
      }

      this.quantity = this.quantity + 1;
    },

    addToCard(item) {
      this.vibrate();

      if (this.blockedByGroupLock()) return;
      if (this.blockedByOncePerOrder(item)) return;

      this.$store.commit("Order/addToCard", {
        item: { ...item, category_id: this.categoryId },
        quantity: this.quantity
      });

      this.close();
    },

    close() {
      this.$store.commit("Order/selectCurrent", {});
    }
  }
};
</script>

<style>
.v-dialog.item-booking-dialog:not(.v-dialog--fullscreen) {
  border-radius: var(--booking-dialog-radius, 12px);
}
</style>

<style scoped>
.item-image {
  cursor: pointer;
  border-radius: 8px;
}

.qty-control {
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 2px 4px;
}

.garnish-card {
  flex: 0 0 33.33%;
  max-width: 33.33%;
}

.garnish-avatar-wrap {
  position: relative;
  display: inline-block;
}

.garnish-removed-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.booked-garnish {
  text-decoration: line-through;
}

.less-padding >>> .v-expansion-panel-content__wrap {
  padding-left: 8px;
  padding-right: 8px;
}

#oldPrice,
#oldPrice-size {
  text-decoration: line-through;
  font-size: 13px;
  padding-top: 3px;
}

#discounted,
#discounted-size {
  font-size: 15px;
  font-weight: bold;
  padding-top: 3px;
}
</style>
